import React, { useState, useEffect } from 'react';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import Link from 'redux-first-router-link';
import CartItems from './CartItems';
import checkoutAnalytics from './checkoutAnalytics';
import Price from '../Product/Price';
import Button from '../Elements/Button/Button';
import ErrorBoundary from '../../hocs/ErrorBoundary/ErrorBoundary';
import { translate, prefix } from '../../utils/locale';
import { atob, splitNewLine, urlify } from '../../utils/string';
import s from './Cart.scss';

const Cart = ({ checkout, setup, cart, productCache, menuPopupHeight, toggleCartHandler, position, cartOpen }) => {
	const { products = {}, productVariantLookup = {} } = productCache?.[setup.market]?.[setup.pricelist] || {};
	const [redirecting, isRedirecting] = useState(false);

	const [noticeShowing, toggelNoticeShowing] = useState(0);
	let notices = [translate(setup, 'checkoutNoItems')];
	if (checkout.selection.items && checkout.selection.items.length) {
		notices = splitNewLine(translate(setup, cart.notices));
	}

	const hasItems = checkout.selection.items && checkout.selection.items.length > 0;
	let subTotal = hasItems && checkout.selection.items.reduce((acc, i) => {
		let itemDetails = checkout.selectionCache.filter((scItem) => scItem && atob(scItem.selectionVariantId) === atob(i.id))[0];
		if (!itemDetails && products) itemDetails = products[i.cacheId];
		const price = i?.price?.[setup.currency]?.price || itemDetails?.price?.[setup.currency]?.price;

		return acc + (parseInt(i.quantity, 10) * parseFloat(price));
	}, 0);
	if (Number.isNaN(subTotal) || subTotal === 'NaN') subTotal = 0;
	let grandTotal = subTotal;

	const updateCheckoutText = () => {
		checkoutAnalytics(checkout.selection.items, setup, checkout.selection.totals, checkout.listItemCache);
		isRedirecting(true);
	};

	const shippingCost = cart.shippingTiers.filter((t) => t.countries && t.countries.indexOf(setup.country) !== -1);
	const unkownShipping = shippingCost.length === 0;
	const freeShipping = shippingCost.length > 0 && subTotal >= shippingCost[0].limit;
	let remaining = 0;
	let progressPercentage = 100;

	if (!unkownShipping && !freeShipping) {
		remaining = shippingCost[0].limit - subTotal;
		progressPercentage = Math.round((subTotal / shippingCost[0].limit) * 100);
		grandTotal += shippingCost[0].cost;
	}

	const [progress, updateProgress] = useState(0);

	useEffect(() => {
		const progressAnimation = window.setTimeout(() => {
			updateProgress(cartOpen ? progressPercentage : 0);
		}, 500);

		return () => {
			window.clearTimeout(progressAnimation);
		};
	}, [cartOpen]);

	useEffect(() => {
		updateProgress(cartOpen ? progressPercentage : 0);
	}, [checkout.selection.items]);

	useEffect(() => {
		const noticeCycle = window.setInterval(() => {
			toggelNoticeShowing((prevState) => (prevState + 1) % notices.length);
		}, 1500);

		return () => {
			window.clearInterval(noticeCycle);
		};
	}, [notices]);


	return (
		<div className={s.cart} style={{ height: `${menuPopupHeight}px` }}>
			<div className={s.cart_header}>
				<a className={s.cart_header_close} href="#close-cart" onClick={toggleCartHandler}>{ translate(setup, cart.return) }</a>
				{ checkout?.selection?.webUrl && <Button variantClasses={['button__transparent', 'button__cartTop']} text={redirecting ? translate(setup, 'goingToCheckout') : translate(setup, cart.checkoutTop)} type="a" location={checkout.selection.webUrl} clickEventHandler={updateCheckoutText} noBlank /> }
			</div>
			<div className={s.cart_subHeader}>
				<h3 className={[s.cart_subHeader_title, (notices.length > 1 ? s.cart_subHeader_title__animation : null)].join(' ')}>{ notices.map((n, i) => <span className={[s.cart_subHeader_title_notice, (i === noticeShowing ? s.cart_subHeader_title_notice__showing : null)].join(' ')} key={urlify(n).slice(0, 20)}>{ n }</span>) }</h3>
				<span className={s.cart_subHeader_amount}>{ checkout.selection?.totals?.totalQuantity || 0 }</span>
			</div>
			<div className={s.cart_items}>
				<ErrorBoundary message="CartItems:" debugData={checkout.selection.items}>
					<CartItems toggleCartHandler={toggleCartHandler} products={products} productVariantLookup={productVariantLookup} />
				</ErrorBoundary>
			</div>
			{
				checkout.selection.items && checkout.selection.items.length > 0 && (
					<div className={[s.cart_totals, checkout.selection.items && checkout.selection.items.length > 0 ? s.cart_totals_hasItems : null].join(' ')}>
						{
							!unkownShipping && (
								<div className={s.cart_totals_progressBar}>
									<span className={s.cart_totals_progressBar_left}>{ freeShipping ? translate(setup, cart.freeShipping) : translate(setup, cart.getFreeShipping) }</span>
									{ !freeShipping && <span className={s.cart_totals_progressBar_right}><Price price={{ price: { [setup.currency]: { price: remaining } } }} setup={setup} /> { translate(setup, cart.left) }</span> }
									<div className={s.cart_totals_progressBar_line}><span style={{ width: `${progress}%` }} /></div>
								</div>
							)
						}
						<div className={s.cart_totals_row}>
							<span className={s.cart_totals_row_left}>{ translate(setup, 'subtotal') }</span>
							<span className={s.cart_totals_row_right}><Price price={{ price: { [setup.currency]: { price: subTotal } } }} setup={setup} /></span>
						</div>
						<div className={s.cart_totals_row}>
							<span className={s.cart_totals_row_left}>{ translate(setup, 'shipping') }</span>
							{ unkownShipping && <span className={s.cart_totals_row_right}>{ translate(setup, 'shippingCalculatedAtCheckout') }</span> }
							{ !unkownShipping && freeShipping && <span className={s.cart_totals_row_right}>{ translate(setup, cart.freeShipping) }</span> }
							{ !unkownShipping && !freeShipping && <span className={s.cart_totals_row_right}><Price price={{ price: { [setup.currency]: { price: shippingCost[0].cost } } }} setup={setup} /></span> }
						</div>
						<div className={[s.cart_totals_row, s.cart_totals_row__grandTotal].join(' ')}>
							<span className={s.cart_totals_row_left}>{ translate(setup, 'total') }</span>
							<span className={s.cart_totals_row_right}><Price price={{ price: { [setup.currency]: { price: grandTotal } } }} setup={setup} /></span>
						</div>
						{
							cart.trustIcons && (
								<div className={s.cart_totals_trustIcons}>
									{ cart.trustIcons.map((i) => <img key={i.key} className={s.cart_totals_trustIcon} src={i.image.url} alt={i.image.alt} />) }
								</div>
							)
						}
						{
							position !== 'selection' && (
								<div className={s.cart_totals_button}>
									<Link className={s.cart_totals_button__return} to={prefix(setup, '/')} onClick={toggleCartHandler}>{ translate(setup, 'goToShop') }</Link>
									<Button variantClasses={['button__cart']} text={redirecting ? translate(setup, 'goingToCheckout') : translate(setup, 'goToCheckout')} type="a" location={checkout.selection.webUrl} clickEventHandler={updateCheckoutText} noBlank />
								</div>
							)
						}
					</div>
				)
			}
		</div>
	);
};

const mapState = ({ checkout, environment, productCache }, props) => {
	const { setup, cart } = environment;
	return { checkout, setup, cart, productCache, ...props };
};

export default hot(module)(connect(mapState)(Cart));
